import fromPairs from 'lodash/fromPairs'
import toPairs from 'lodash/toPairs'
import * as Redux from 'redux'
import * as ReduxObservable from 'redux-observable'
import 'rxjs/add/operator/do'
import 'rxjs/add/operator/filter'
import 'rxjs/add/operator/mapTo'
import 'rxjs/add/operator/skipWhile'
import 'rxjs/add/operator/take'
import * as RootConstants from '../constants'
import * as AuthConstants from '../pages/auth/constants'
import * as Auth from '../pages/auth/utils'
import * as CommunityConfig from '../pages/community-config'
import * as Request from '../request'
import {ApplicationState} from '../rootReducer'
import * as Router from '../router'
import * as Actions from './actions'
import * as Constants from './constants'
import * as Types from './types'

export const onEnter: ReduxObservable.Epic<Redux.Action, any> = (action$, state$, {gapi, history}) =>
  action$.ofType(Router.ON_ENTER)
    .map(() => {
        return { type: Constants.APP_ENTER_STARTED }
      })

export const setCommunityInLocalStorage = (
  action$: ReduxObservable.ActionsObservable<Redux.Action>,
  store: Redux.Store<ApplicationState>,
  {localStorage}: Types.Dependencies,
) => action$.ofType(Constants.SELECT_COMMUNITY)
  .filter(({communityIdentifier}: Types.SelectCommunityAction) => Boolean(communityIdentifier))
  .do(({communityIdentifier}: Types.SelectCommunityAction) =>
    localStorage.setItem(Constants.SELECTED_COMMUNITY, communityIdentifier))
  .mapTo({type: Constants.COMMUNITY_SET_IN_LOCAL_STORAGE})

export const setSelectedCommunity = (
  action$: ReduxObservable.ActionsObservable<Redux.Action>,
  store: Redux.Store<ApplicationState>,
  {history, localStorage}: Types.Dependencies,
) => action$.ofType(Constants.INITIALIZING_APPLICATION)
  .filter(() => Auth.pageNeedsAuthentication(history))
  .filter(() => Boolean(localStorage.getItem(RootConstants.ACCESS_TOKEN)))
  .filter(() => localStorage.getItem(Constants.SELECTED_COMMUNITY))
  .map(() => Actions.selectCommunity(localStorage.getItem(Constants.SELECTED_COMMUNITY)))

export const setSelectedCommunityDefault = (
  action$: ReduxObservable.ActionsObservable<Redux.Action>,
  store: Redux.Store<ApplicationState>,
  {localStorage}: Types.Dependencies,
) => action$.ofType(Request.successConstant(CommunityConfig.FETCH_COMMUNITIES))
  .filter(() => !localStorage.getItem(Constants.SELECTED_COMMUNITY))
  .filter((action: CommunityConfig.RecieivingCommunitiesSuccessAction) =>
    action.payload.response.length > 0)
  .map((action: CommunityConfig.RecieivingCommunitiesSuccessAction) =>
    Actions.selectCommunity(action.payload.response[0].identifier))

export const alertNoSelectedCommunity: ReduxObservable.Epic<any, any> =
  (action$, store, {raven}) =>
    action$.ofType(Request.successConstant(CommunityConfig.FETCH_COMMUNITIES))
      .filter(() => !localStorage.getItem(Constants.SELECTED_COMMUNITY))
      .filter((action) => action.payload.response.length < 1)
      .do(() => {
        raven.captureException(new Error(`${CommunityConfig.FETCH_COMMUNITIES} returned no communities.`))
      })
      .mapTo({type: 'ERROR'})

export const fetchConfig = (
  action$: ReduxObservable.ActionsObservable<Redux.Action>,
  store: Redux.Store<ApplicationState>,
) => action$.ofType(Constants.SELECT_COMMUNITY)
  .map(() => CommunityConfig.fetchCommunityConfig(store.getState().app.selectedCommunity))

export const mediaQueries: ReduxObservable.Epic<Redux.Action, any> = (action$, store, {window}) =>
  action$.ofType(Constants.INITIALIZING_APPLICATION)
    .map(() => Actions.mediaQuerySet(window))

export const fetchRasaToken = (
  action$: ReduxObservable.ActionsObservable<Types.SelectCommunityAction>,
) => action$.ofType(Constants.SELECT_COMMUNITY)
  .map(({communityIdentifier}) =>
    Request.requestActionCreator(Constants.FETCH_RASA_TOKEN)({method: 'GET', url: `/token/${communityIdentifier}`}))

const fetchRasaTokenError = (
  action$: ReduxObservable.ActionsObservable<any>,
) => action$.ofType(Request.errorConstant(Constants.FETCH_RASA_TOKEN))
  .filter((errorResponse) => errorResponse.payload.error.status === 500)
  .mergeMap(() => [
    localStorage.removeItem(Constants.SELECTED_COMMUNITY),
    location.reload(),
  ])

let initRefCount = 0 // THIS IS BAD NEVER DO THIS ANYWHERE EVER AGAIN
export const appInitWindow: ReduxObservable.Epic<Redux.Action, any> = (action$) =>
  action$.ofType(Constants.INITIALIZING_APPLICATION)
    .mergeMap(() =>
      action$
        .filter((action) => action.type.includes(Request.REQUEST))
        .do(() => initRefCount++)
        .mergeMap((requestAction) =>
          action$
            .filter((action) =>
              action.type.includes(requestAction.type.replace(Request.REQUEST, Request.SUCCESS)) ||
              action.type.includes(requestAction.type.replace(Request.REQUEST, Request.ERROR)))
            .do(() => initRefCount--)
            .take(1)))
    .skipWhile(() => initRefCount !== 0)
    .take(1)
    .mapTo(({type: Constants.CONTENT_INIT}))

let contentInitRefCount = 0 // THIS IS BAD NEVER DO THIS ANYWHERE EVER AGAIN
export const contentInitWindow: ReduxObservable.Epic<any, any> = (action$) =>
  action$.ofType(Constants.CONTENT_INIT)
    .mergeMap(() =>
      action$
        .filter((action) => action.type.includes(Request.REQUEST) && !action.options.skipLifeCycle)
        .do(() => contentInitRefCount++)
        .mergeMap((requestAction) =>
          action$
            .filter((action) =>
              action.type.includes(requestAction.type.replace(Request.REQUEST, Request.SUCCESS)) ||
              action.type.includes(requestAction.type.replace(Request.REQUEST, Request.ERROR)))
            .do(() => contentInitRefCount--)
            .take(1)))
    .skipWhile(() => contentInitRefCount !== 0)
    .take(1)
    .mapTo(({type: Constants.VIEW_INIT}))

const urlParse: ReduxObservable.Epic<any, any> = (action$, store, {window}) =>
  action$
    .ofType(Constants.INITIALIZING_APPLICATION)
    .map((action) => new window.URLSearchParams(window.location.search))
    .map((params) => fromPairs(Array.from(params.entries())))
    .map(Actions.urlParsed)

const authenticated: ReduxObservable.Epic<any, any> = (action$, store) =>
  action$
    .ofType(Request.successConstant(AuthConstants.VALIDATE_TOKEN))
    .map(() => ({type: Constants.AUTHENTICATED}))

const updateUrlParams: ReduxObservable.Epic<any, any> = (action$, store, {window}) =>
  action$
    .ofType(Constants.UPDATE_URL_PARAMS)
    .map((action) => ({action, params: new window.URLSearchParams(window.location.search)}))
    .do(({action, params}) =>
      toPairs(action.params).map(([key, value]) =>
        params.has(key) ?
          params.set(key, value) :
          params.append(key, value)))
    // delete params with no value
    .do(({action, params}) =>
      toPairs(action.params)
        .filter(([key, value]) => !value)
        .filter(([key, value]) => params.has(key))
        .map(([key, value]) => params.delete(key)))
    .mergeMap(({params}) => [
      Router.replace(`${window.location.pathname}?${params}`),
      Actions.urlParsed(fromPairs(Array.from(params.entries()))),
    ])

export const epic = ReduxObservable.combineEpics(
  alertNoSelectedCommunity,
  appInitWindow,
  authenticated,
  contentInitWindow,
  fetchConfig,
  fetchRasaToken,
  fetchRasaTokenError,
  mediaQueries,
  setCommunityInLocalStorage,
  setSelectedCommunity,
  setSelectedCommunityDefault,
  urlParse,
  updateUrlParams,
)



// WEBPACK FOOTER //
// ./src/app/epic.ts